import { useQuery, useZero } from "@rocicorp/zero/react";
import { keyframes, styled } from "goober";
import { nanoid } from "nanoid";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { sharedMutators } from "../../shared/mutators";
import { queries } from "../../shared/queries";
import { isRouteType } from "../../shared/stepConfig";
import { ATTACHMENTS_VOLUME } from "../../shared/volumes/storagePath";
import { useCommitFiles } from "../hooks/useCommitFiles";
import { useStepTestExecutions } from "../hooks/useStepExecutions";
import {
  changedRouteAccess,
  groupsKey,
  isRouteAccessDisabled,
} from "../lib/routeAccessChanges";
import { config } from "../environment";
import {
  ROUTE_AUTH_BADGE_COLORS,
  routeAuthDescription,
  routeAuthLabel,
} from "../lib/routeAuthDisplay";
import { ROUTE_TYPE_ICONS } from "../lib/routeTypeDisplay";
import { stepDirsWithStepTests } from "../lib/stepTestDirs";
import {
  computeStepTestFailures,
  STEP_TEST_TIMEOUT_MS,
  type StepRunStatus,
  type StepFailure,
  stepRunStatus,
  type TestExecution,
} from "../lib/stepTestFailures";
import { useMakeUrl, routes } from "../lib/urls";
import { Banner } from "./Banner";
import { Button } from "./Button";
import { Icon } from "./Icon";
import { Modal } from "./Modal";
import { PressButton } from "./PressButton";
import { ModalContent, ModalFooter, ModalHead } from "./ModalCard";
import { PreviewListBox } from "./PreviewList";
import { Spinner } from "./Spinner";
import { StatusBadge } from "./StatusBadge";
import { Text } from "./Text";
import { Tooltip } from "./Tooltip";

const StepTestRow = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px;
  border-radius: 8px;
`;

const RowHead = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const NameSlot = styled("div")`
  flex: 1;
  min-width: 0;
`;

const StatusSlot = styled("div")`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const ChangedRouteRow = styled("a")`
  all: unset;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  min-width: 0;

  &:hover {
    background: var(--alpha4);
  }
`;

const AuthChange = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const PUSH_LIVE_DELAY_MS = 2_000;

const fillPushLiveButton = keyframes`
  from {
    transform: scaleX(0);
  }
  to {
    transform: scaleX(1);
  }
`;

const StepTestsPushLive = styled(Button)`
  position: relative;
  isolation: isolate;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    z-index: -1;
    background: color-mix(in srgb, var(--control-fg) 20%, transparent);
    transform-origin: left;
    transform: scaleX(0);
    pointer-events: none;
  }

  &[data-counting-down="true"]::before {
    animation: ${fillPushLiveButton} ${PUSH_LIVE_DELAY_MS}ms linear forwards;
  }

  @media (prefers-reduced-motion: reduce) {
    &[data-counting-down="true"]::before {
      animation: none;
      transform: scaleX(1);
    }
  }
`;

const FixFailuresButton = styled(Button)`
  margin-right: auto;
`;

type PushLiveButtonState = "waiting" | "countdown" | "ready";

export function StepTestsPushLiveButton({
  state,
  onProceed,
}: {
  state: PushLiveButtonState;
  onProceed: () => void;
}) {
  const proceededRef = useRef(false);
  // Stable identity keeps the two-second deadline from restarting on parent renders.
  const proceed = useCallback(() => {
    if (proceededRef.current) return;
    proceededRef.current = true;
    onProceed();
  }, [onProceed]);

  useEffect(() => {
    if (state !== "countdown") return;
    const timeout = setTimeout(proceed, PUSH_LIVE_DELAY_MS);
    return () => clearTimeout(timeout);
  }, [state, proceed]);

  return (
    <StepTestsPushLive
      size="l"
      disabled={state === "waiting"}
      data-counting-down={state === "countdown"}
      onClick={proceed}
    >
      Push live
    </StepTestsPushLive>
  );
}

function StepStatusIcon({ status }: { status: StepRunStatus }) {
  if (status === "running") return <Spinner size={16} />;
  if (status === "passed")
    return <Icon name="check-filled" size={16} color="var(--green-600)" />;
  return <Icon name="check-filled-x" size={16} color="var(--red-600)" />;
}

type Props = {
  workflowId: string;
  branchId: string;
  spaceId: string;
  onProceed: (copyVolumesToLive: boolean) => void;
  onCancel: () => void;
  onInvestigateFailures?: (failures: readonly StepFailure[]) => void;
};

// Independent gates run before a push, each its own modal: first the step
// tests (silent when they pass), then a confirmation for any link whose access
// scope changed, then a choice about the draft's volume data. Each stage
// advances itself when it has nothing to show, so a clean push flashes none.
export function PushLivePreflight({
  workflowId,
  branchId,
  spaceId,
  onProceed,
  onCancel,
  onInvestigateFailures,
}: Props) {
  const [stage, setStage] = useState<"tests" | "access" | "volumes">("tests");
  const goToAccess = () => setStage("access");
  const goToVolumes = () => setStage("volumes");

  if (stage === "tests") {
    return (
      <StepTestsGate
        workflowId={workflowId}
        branchId={branchId}
        onInvestigateFailures={onInvestigateFailures}
        onProceed={goToAccess}
        onCancel={onCancel}
      />
    );
  }
  if (stage === "access") {
    return (
      <AccessChangeGate
        workflowId={workflowId}
        branchId={branchId}
        spaceId={spaceId}
        onProceed={goToVolumes}
        onCancel={onCancel}
      />
    );
  }
  return (
    <VolumeDataGate
      branchId={branchId}
      spaceId={spaceId}
      onProceed={onProceed}
      onCancel={onCancel}
    />
  );
}

function StepTestsGate({
  workflowId,
  branchId,
  onProceed,
  onCancel,
  onInvestigateFailures,
}: {
  workflowId: string;
  branchId: string;
  onProceed: () => void;
  onCancel: () => void;
  onInvestigateFailures?: (failures: readonly StepFailure[]) => void;
}) {
  const z = useZero();

  const [branch, branchResult] = useQuery(
    queries.loadBranchMetadata({ workflowId, branchId })
  );
  const commitId = branch?.headCommitId ?? null;
  const [headCommitFiles, headCommitFilesResult] = useCommitFiles(
    commitId ?? ""
  );
  const testStepDirs = useMemo(
    () => stepDirsWithStepTests(headCommitFiles),
    [headCommitFiles]
  );

  const [runs, setRuns] = useState<
    { stepDir: string; executionId: string }[] | null
  >(null);
  const [timedOut, setTimedOut] = useState(false);
  const startedRef = useRef(false);

  // Once the branch's files have loaded, kick off a fresh step-test run per step
  // that has step tests. No step tests → nothing to gate, advance immediately.
  useEffect(() => {
    if (startedRef.current) return;
    if (
      branchResult.type !== "complete" ||
      headCommitFilesResult.type !== "complete"
    )
      return;
    startedRef.current = true;
    if (!commitId || testStepDirs.length === 0) {
      onProceed();
      return;
    }
    // oxlint-disable-next-line react/react-compiler -- EffectSetState: kicks off the one-shot step-test runs via z.mutate once the branch files finish loading
    setRuns(
      testStepDirs.map((stepDir) => {
        const executionId = nanoid(12);
        z.mutate(
          sharedMutators.runStepTests({
            runId: nanoid(12),
            workflowId,
            commitId,
            stepDir,
            executionId,
          })
        );
        return { stepDir, executionId };
      })
    );
  }, [
    branchResult.type,
    headCommitFilesResult.type,
    testStepDirs,
    commitId,
    z,
    workflowId,
    onProceed,
  ]);

  useEffect(() => {
    if (!runs) return;
    const t = setTimeout(() => setTimedOut(true), STEP_TEST_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [runs]);

  const { executions: testExecutions } = useStepTestExecutions(
    workflowId,
    branchId
  );
  const byId = useMemo(() => {
    const map = new Map<string, TestExecution>();
    for (const e of testExecutions) map.set(e.id, e);
    return map;
  }, [testExecutions]);

  const execs = runs?.map((r) => byId.get(r.executionId)) ?? [];
  const total = runs?.length ?? 0;
  const completed = execs.filter((e) => e && e.status !== "pending").length;
  const allTerminal = runs != null && completed === total;

  // Spec rows commit in the same transaction as the execution's terminal
  // status, so once every run is terminal we can judge immediately.
  const failures =
    runs && (allTerminal || timedOut)
      ? computeStepTestFailures(runs, byId, timedOut)
      : null;
  const pushLiveButtonState: PushLiveButtonState =
    failures == null
      ? "waiting"
      : failures.length === 0
        ? "countdown"
        : "ready";

  return (
    <Modal open onClose={onCancel} alert>
      <ModalContent style={{ width: 320 }}>
        <ModalHead>
          <Text variant="text-regular-md" color="var(--text-primary)">
            Step tests
          </Text>
          <Text variant="text-regular" color="var(--text-secondary)">
            We run these on the latest version before pushing live.
          </Text>
        </ModalHead>
        <PreviewListBox style={{ maxHeight: 280 }}>
          {runs && runs.length > 0 ? (
            runs.map((r) => {
              const status = stepRunStatus(r, byId);
              const rowContent = (
                <>
                  <NameSlot>
                    <Text
                      variant="text-regular"
                      color="var(--text-primary)"
                      ellipsis
                    >
                      {r.stepDir}
                    </Text>
                  </NameSlot>
                  <StatusSlot>
                    <StepStatusIcon status={status} />
                  </StatusSlot>
                </>
              );
              return (
                <StepTestRow key={r.stepDir}>
                  <RowHead>{rowContent}</RowHead>
                </StepTestRow>
              );
            })
          ) : (
            <StepTestRow>
              <RowHead>
                <NameSlot>
                  <Text variant="text-regular" color="var(--text-secondary)">
                    Preparing…
                  </Text>
                </NameSlot>
                <StatusSlot>
                  <Spinner size={16} />
                </StatusSlot>
              </RowHead>
            </StepTestRow>
          )}
        </PreviewListBox>
        <ModalFooter>
          {onInvestigateFailures && failures && failures.length > 0 && (
            <FixFailuresButton
              size="l"
              variant="subtle"
              icon="radar"
              onClick={() => {
                onCancel();
                onInvestigateFailures(failures);
              }}
            >
              Fix failures
            </FixFailuresButton>
          )}
          <Button size="l" variant="subtle" onClick={onCancel}>
            Cancel
          </Button>
          <StepTestsPushLiveButton
            state={pushLiveButtonState}
            onProceed={onProceed}
          />
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function AccessChangeGate({
  workflowId,
  branchId,
  spaceId,
  onProceed,
  onCancel,
}: {
  workflowId: string;
  branchId: string;
  spaceId: string;
  onProceed: () => void;
  onCancel: () => void;
}) {
  const makeUrl = useMakeUrl();
  const [tenant, tenantResult] = useQuery(queries.currentTenant());
  const [draft, draftResult] = useQuery(
    queries.loadBranchMetadata({ workflowId, branchId })
  );
  // Resolve the live scope from the workflow's main branch directly, so a query
  // that completes with no main branch (a first publish) is distinguishable
  // from one that simply hasn't loaded — the gate must not fail open.
  const [mainWorkflow, mainResult] = useQuery(
    queries.workflowMainRoutes({ workflowId })
  );

  const changes = useMemo(
    () =>
      changedRouteAccess(
        draft?.headCommit?.routes ?? [],
        mainWorkflow?.branches?.headCommit?.routes ?? [],
        config.routeAuthForceDisabled,
        tenant
      ),
    [
      draft?.headCommit?.routes,
      mainWorkflow?.branches?.headCommit?.routes,
      tenant,
    ]
  );

  const publicAccessUnavailable = (draft?.headCommit?.routes ?? []).some(
    (route) =>
      isRouteAccessDisabled(route, config.routeAuthForceDisabled, tenant)
  );

  const errored =
    draftResult.type === "error" ||
    mainResult.type === "error" ||
    tenantResult.type === "error" ||
    (tenantResult.type === "complete" && !tenant);
  const resolved =
    draftResult.type === "complete" &&
    mainResult.type === "complete" &&
    tenantResult.type === "complete" &&
    !!tenant;
  const warn = resolved && changes.length > 0;

  const proceededRef = useRef(false);
  useEffect(() => {
    if (proceededRef.current) return;
    if (errored || !resolved || warn) return;
    proceededRef.current = true;
    onProceed();
  }, [errored, resolved, warn, onProceed]);

  if (errored || !resolved) {
    return (
      <Modal open onClose={onCancel} alert>
        <ModalContent style={{ width: 320 }}>
          <ModalHead>
            <Text variant="text-regular-md" color="var(--text-primary)">
              Before you push live
            </Text>
          </ModalHead>
          <PreviewListBox style={{ maxHeight: 280 }}>
            <StepTestRow>
              <RowHead>
                <NameSlot>
                  <Text variant="text-regular" color="var(--text-secondary)">
                    {errored
                      ? "We couldn’t check link access. Cancel and try pushing live again."
                      : "Checking link access…"}
                  </Text>
                </NameSlot>
                {!errored && (
                  <StatusSlot>
                    <Spinner size={16} />
                  </StatusSlot>
                )}
              </RowHead>
            </StepTestRow>
          </PreviewListBox>
          <ModalFooter>
            <Button size="l" variant="subtle" onClick={onCancel}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }

  if (!warn) return null;

  return (
    <Modal open onClose={onCancel} alert>
      <ModalContent style={{ width: 320 }}>
        <ModalHead>
          <Text variant="text-regular-md" color="var(--text-primary)">
            Before you push live
          </Text>
          <Text variant="text-regular" color="var(--text-secondary)">
            {publicAccessUnavailable
              ? "Review link access before pushing live."
              : "Access to these links changed in this draft."}
          </Text>
        </ModalHead>
        {publicAccessUnavailable && (
          <Banner>
            {config.routeAuthForceDisabled && tenant?.isExploreSignup
              ? "Public access isn’t available in Explore edition. You can’t push live with public links or External ID webpages. Use Space or Tenant access to require sign-in, or upgrade to a paid plan for public access."
              : "Your administrator has disabled access for one or more links in this workflow. You can’t push live until those links use an allowed access setting. Use Space or Tenant access to require sign-in."}
          </Banner>
        )}
        <PreviewListBox style={{ maxHeight: 280 }}>
          {changes.map((change) => {
            const { route, stepDir, title, routeType, from, to } = change;
            const scopeChanged =
              from !== to ||
              groupsKey(change.fromGroups) !== groupsKey(change.toGroups);
            const iconName = isRouteType(routeType)
              ? ROUTE_TYPE_ICONS[routeType]
              : "link-2";
            return (
              <ChangedRouteRow
                key={route}
                href={makeUrl(routes.space.workflow, {
                  spaceId,
                  workflowId,
                  branchId,
                  step: stepDir,
                })}
                target="_blank"
                rel="noopener noreferrer"
              >
                <StatusSlot>
                  <Icon name={iconName} size={16} color="var(--text-primary)" />
                </StatusSlot>
                <NameSlot>
                  <Text
                    variant="text-regular"
                    color="var(--text-primary)"
                    ellipsis
                  >
                    {title || route}
                  </Text>
                </NameSlot>
                <AuthChange>
                  {!scopeChanged ? null : from === null ? (
                    <Tooltip content="This link is new in this draft.">
                      <StatusBadge color="gray">New</StatusBadge>
                    </Tooltip>
                  ) : (
                    <Tooltip
                      content={routeAuthDescription(from, change.fromGroups)}
                    >
                      <StatusBadge color={ROUTE_AUTH_BADGE_COLORS[from]}>
                        {routeAuthLabel(from, change.fromGroups)}
                      </StatusBadge>
                    </Tooltip>
                  )}
                  {scopeChanged && (
                    <Text variant="text-small" color="var(--text-tertiary)">
                      →
                    </Text>
                  )}
                  <Tooltip
                    content={
                      isRouteAccessDisabled(
                        {
                          routeType,
                          routeAuth: to,
                        },
                        config.routeAuthForceDisabled,
                        tenant
                      )
                        ? "This link won’t work because public access is disabled."
                        : routeAuthDescription(to, change.toGroups)
                    }
                  >
                    <StatusBadge color={ROUTE_AUTH_BADGE_COLORS[to]}>
                      {routeAuthLabel(to, change.toGroups)}
                    </StatusBadge>
                  </Tooltip>
                </AuthChange>
              </ChangedRouteRow>
            );
          })}
        </PreviewListBox>
        <ModalFooter>
          <Button size="l" variant="subtle" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            size="l"
            onClick={onProceed}
            disabled={publicAccessUnavailable}
          >
            Push live
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

// A draft's volume data is discarded when it goes live; the pusher can instead
// seed the space's live volumes with it, but only where live has no data yet.
function VolumeDataGate({
  branchId,
  spaceId,
  onProceed,
  onCancel,
}: {
  branchId: string;
  spaceId: string;
  onProceed: (copyVolumesToLive: boolean) => void;
  onCancel: () => void;
}) {
  const [spaceVolumes, spaceVolumesResult] = useQuery(
    queries.spaceVolumes({ spaceId })
  );
  const settled = spaceVolumesResult.type !== "unknown";
  const liveVolumeNames = new Set(
    spaceVolumes
      .filter((volume) => volume.branch?.name === "main")
      .map((volume) => volume.name)
  );
  const show =
    settled &&
    spaceVolumes.some(
      (volume) =>
        volume.branchId === branchId &&
        volume.name !== ATTACHMENTS_VOLUME &&
        !liveVolumeNames.has(volume.name)
    );

  const proceededRef = useRef(false);
  useEffect(() => {
    if (proceededRef.current || !settled || show) return;
    proceededRef.current = true;
    onProceed(false);
  }, [settled, show, onProceed]);

  const [copy, setCopy] = useState(false);
  if (!show) return null;

  return (
    <Modal open onClose={onCancel} alert>
      <ModalContent style={{ width: 340 }}>
        <ModalHead>
          <Text variant="text-regular-md" color="var(--text-primary)">
            Before you push live
          </Text>
          <Text variant="text-regular" color="var(--text-secondary)">
            This draft has storage volumes that live doesn’t have yet. Draft
            storage is discarded once it’s live.
          </Text>
        </ModalHead>
        <PreviewListBox role="radiogroup" style={{ maxHeight: "none" }}>
          <VolumeChoiceRow
            selected={!copy}
            label="Start live with empty storage"
            onPress={() => setCopy(false)}
          />
          <VolumeChoiceRow
            selected={copy}
            label="Copy this draft’s data to live"
            description="Volumes that already hold data on live are left as they are."
            onPress={() => setCopy(true)}
          />
        </PreviewListBox>
        <ModalFooter>
          <Button size="l" variant="subtle" onClick={onCancel}>
            Cancel
          </Button>
          <Button size="l" onClick={() => onProceed(copy)}>
            Push live
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

const VolumeChoiceButton = styled(PressButton)`
  all: unset;
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 8px;
  box-sizing: border-box;
  border-radius: 8px;
  cursor: pointer;
  min-width: 0;
  &:hover {
    background: var(--alpha4);
  }
`;

const VolumeChoiceText = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

function VolumeChoiceRow({
  selected,
  label,
  description,
  onPress,
}: {
  selected: boolean;
  label: string;
  description?: string;
  onPress: () => void;
}) {
  return (
    <VolumeChoiceButton role="radio" aria-checked={selected} onPress={onPress}>
      <VolumeChoiceText>
        <Text variant="text-regular" color="var(--text-primary)">
          {label}
        </Text>
        {description && (
          <Text variant="text-small" color="var(--text-secondary)">
            {description}
          </Text>
        )}
      </VolumeChoiceText>
      <span style={{ display: "inline-flex", opacity: selected ? 1 : 0.12 }}>
        <Icon name={selected ? "check-filled" : "check-empty"} size={16} />
      </span>
    </VolumeChoiceButton>
  );
}
